import { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { AuthContext } from '../contexts/AuthContext';
import './MyKits.css';

const API_URL = process.env.REACT_APP_API_URL;

const r2 = (n) => Math.round(n * 100) / 100;

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload  = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

const KitPaymentModal = ({ order, user, onClose, onPaid }) => {
  const auth = { Authorization: `Bearer ${user?.token}` };
  const [walletBalance, setWalletBalance] = useState(null);
  const [cashUsed, setCashUsed]     = useState(0);
  const [pointsUsed, setPointsUsed] = useState(0);
  const [phase, setPhase]           = useState('loading');
  const [error, setError]           = useState('');
  const [rzOrderId, setRzOrderId]   = useState(null);

  const orderAmount = parseFloat(order.parent_order_amount);

  useEffect(() => {
    axios.get(`${API_URL}/wallet/balance`, { headers: auth })
      .then(res => {
        const bal = res.data;
        setWalletBalance(bal);
        const maxCash   = r2(Math.min(parseFloat(bal.cash_balance   || 0), orderAmount));
        const maxPoints = r2(Math.min(parseFloat(bal.points_balance || 0), Math.max(0, orderAmount - maxCash)));
        setCashUsed(maxCash);
        setPointsUsed(maxPoints);
        setPhase('ready');
      })
      .catch(() => { setWalletBalance(null); setPhase('ready'); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasCash   = parseFloat(walletBalance?.cash_balance   || 0) > 0;
  const hasPoints = parseFloat(walletBalance?.points_balance || 0) > 0;
  const hasWallet = hasCash || hasPoints;
  const razorpayDue = r2(Math.max(0, orderAmount - cashUsed - pointsUsed));

  const cancelPending = (rzId) => {
    if (!rzId) return;
    axios.post(`${API_URL}/pay/pay-existing/cancel`, { razorpay_order_id: rzId }, { headers: auth })
      .catch(() => {});
    setRzOrderId(null);
  };

  const handlePay = async () => {
    setError('');
    setPhase('processing');
    const payload = {
      order_id:            order.parent_order_id,
      wallet_cash_amount:  cashUsed,
      wallet_points_amount: pointsUsed,
    };

    if (razorpayDue < 0.01 && (cashUsed > 0 || pointsUsed > 0)) {
      try {
        await axios.post(`${API_URL}/pay/pay-existing/create-order`, payload, { headers: auth });
        onPaid();
      } catch (err) {
        setError(err.response?.data?.error || 'Payment failed. Please try again.');
        setPhase('ready');
      }
      return;
    }

    const loaded = await loadRazorpayScript();
    if (!loaded) {
      setError('Failed to load payment SDK. Please check your connection.');
      setPhase('ready');
      return;
    }

    try {
      const { data } = await axios.post(`${API_URL}/pay/pay-existing/create-order`, payload, { headers: auth });
      setRzOrderId(data.razorpay_order_id);

      const rzp = new window.Razorpay({
        key:      data.key,
        amount:   data.amount,
        currency: data.currency,
        order_id: data.razorpay_order_id,
        name:        'Local Heros',
        description: `Payment for order ${order.parent_order_no}`,
        prefill: { name: user?.name, email: user?.email, contact: user?.phone },
        theme: { color: '#C9A227' },
        handler: async (response) => {
          try {
            await axios.post(`${API_URL}/pay/pay-existing/verify`, {
              order_id:            order.parent_order_id,
              razorpay_order_id:   response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature:  response.razorpay_signature,
              wallet_cash_amount:  cashUsed,
              wallet_points_amount: pointsUsed,
            }, { headers: auth });
            onPaid();
          } catch (err) {
            setError(err.response?.data?.error || 'Payment received but order update failed. Please contact support.');
            setPhase('ready');
          }
        },
        modal: {
          ondismiss: () => { cancelPending(data.razorpay_order_id); setPhase('ready'); },
        },
      });
      rzp.on('payment.failed', (response) => {
        cancelPending(data.razorpay_order_id);
        setError(`Payment failed: ${response.error.description}`);
        setPhase('ready');
      });
      rzp.open();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to initiate payment. Please try again.');
      setPhase('ready');
    }
  };

  const btnLabel = razorpayDue < 0.01
    ? 'Pay with Wallet'
    : (cashUsed > 0 || pointsUsed > 0)
      ? `Pay ₹${razorpayDue.toFixed(2)} via Razorpay`
      : `Pay ₹${orderAmount.toFixed(2)}`;

  return (
    <div className="myk-modal-overlay" onClick={() => phase !== 'processing' && onClose()}>
      <div className="myk-modal" onClick={e => e.stopPropagation()}>
        <div className="myk-modal-header">
          <h3>Pay for Order {order.parent_order_no}</h3>
          <button className="myk-modal-close" onClick={onClose} disabled={phase === 'processing'}>×</button>
        </div>
        <div className="myk-modal-body">
          {phase === 'loading' && <p className="myk-empty">Loading…</p>}
          {error && <p className="myk-error">{error}</p>}
          {phase !== 'loading' && (
            <>
              <div className="myk-pay-amount-row">
                <span>Order Total</span>
                <strong>₹{orderAmount.toFixed(2)}</strong>
              </div>
              {hasWallet && (
                <div className="myk-pay-wallet-section">
                  {hasCash && (
                    <label className="myk-pay-wallet-row">
                      <span>Wallet Cash (₹{parseFloat(walletBalance.cash_balance).toFixed(2)} available)</span>
                      <input
                        type="number" min={0} max={parseFloat(walletBalance.cash_balance)}
                        step="0.01" value={cashUsed}
                        onChange={e => setCashUsed(r2(Math.min(parseFloat(e.target.value) || 0, parseFloat(walletBalance.cash_balance), orderAmount)))}
                        className="myk-pay-wallet-input"
                        disabled={phase === 'processing'}
                      />
                    </label>
                  )}
                  {hasPoints && (
                    <label className="myk-pay-wallet-row">
                      <span>Wallet Points (₹{parseFloat(walletBalance.points_balance).toFixed(2)} available)</span>
                      <input
                        type="number" min={0} max={parseFloat(walletBalance.points_balance)}
                        step="0.01" value={pointsUsed}
                        onChange={e => setPointsUsed(r2(Math.min(parseFloat(e.target.value) || 0, parseFloat(walletBalance.points_balance), Math.max(0, orderAmount - cashUsed))))}
                        className="myk-pay-wallet-input"
                        disabled={phase === 'processing'}
                      />
                    </label>
                  )}
                  {razorpayDue > 0.01 && (
                    <div className="myk-pay-amount-row">
                      <span>To pay via Razorpay</span>
                      <strong>₹{razorpayDue.toFixed(2)}</strong>
                    </div>
                  )}
                </div>
              )}
              <div className="myk-modal-footer">
                <button className="myk-btn myk-btn--back" onClick={onClose} disabled={phase === 'processing'}>Cancel</button>
                <button
                  className="myk-btn myk-btn--pay"
                  onClick={handlePay}
                  disabled={phase === 'processing' || (razorpayDue < 0.01 && cashUsed < 0.01 && pointsUsed < 0.01)}
                >
                  {phase === 'processing' ? 'Processing…' : btnLabel}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const fmtDate = (s) => {
  if (!s) return '—';
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-IN', {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
  });
};

const STATE_ICON = {
  Delivered:   '✅',
  Dispatched:  '🚚',
  Scheduled:   '📦',
  Rescheduled: '🔄',
  Cancelled:   '❌',
};

const isActionable = (delivery) => {
  if (['Delivered', 'Dispatched', 'Cancelled'].includes(delivery.order_state)) return false;
  const today = new Date().toISOString().split('T')[0];
  return delivery.delivery_date > today;
};

// Mirror of the backend helper — returns { DOW: multiplier }
// e.g. deliveryDays=[0,3], consumptionDays=[1,2,3,4,5] → { 0:3, 3:2 }
function itemsPerDelivery(deliveryDays, consumptionDays) {
  if (!deliveryDays?.length || !consumptionDays?.length) return {};
  const sorted = [...deliveryDays].sort((a, b) => a - b);
  const result = {};
  for (let i = 0; i < sorted.length; i++) {
    const d    = sorted[i];
    const next = sorted[(i + 1) % sorted.length];
    let count  = 0;
    if (next > d) {
      for (const c of consumptionDays) { if (c > d && c <= next) count++; }
    } else {
      for (const c of consumptionDays) { if (c > d || c <= next) count++; }
    }
    result[d] = count;
  }
  return result;
}

function dowOf(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).getDay();
}

export default function MyKits() {
  const { user } = useContext(AuthContext);
  const auth = () => ({ headers: { Authorization: `Bearer ${user?.token}` } });

  const [parentOrders, setParentOrders] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');
  const [expanded, setExpanded]         = useState(new Set());
  const [payingOrder, setPayingOrder]   = useState(null);

  // Reschedule modal
  const [rescheduleModal, setRescheduleModal] = useState(null); // { delivery, kitScheduleDays }
  const [validDates, setValidDates]           = useState([]);
  const [rescheduleLoading, setRescheduleLoading] = useState(false);
  const [rescheduleError, setRescheduleError]     = useState('');

  // Cancel modal
  const [cancelModal, setCancelModal]     = useState(null); // { delivery, kitItems }
  const [cancelConfirmText, setCancelConfirmText] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError]     = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await axios.get(`${API_URL}/kit-orders/my-kits`, auth());
      setParentOrders(res.data);
    } catch {
      setError('Failed to load your kit orders.');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const toggleParent = (parentId) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(parentId) ? next.delete(parentId) : next.add(parentId);
      return next;
    });
  };

  // ── Reschedule ────────────────────────────────────────────
  const openReschedule = async (delivery, scheduleDays) => {
    setRescheduleError('');
    setRescheduleModal({ delivery, scheduleDays });
    try {
      const res = await axios.get(`${API_URL}/kit-orders/child/${delivery.id}/valid-dates`, auth());
      setValidDates(res.data.dates || []);
    } catch {
      setRescheduleError('Could not load available dates.');
      setValidDates([]);
    }
  };

  const confirmReschedule = async (newDate) => {
    if (!newDate || !rescheduleModal) return;
    setRescheduleLoading(true);
    setRescheduleError('');
    try {
      await axios.patch(
        `${API_URL}/kit-orders/child/${rescheduleModal.delivery.id}/reschedule`,
        { new_date: newDate },
        auth()
      );
      setRescheduleModal(null);
      setValidDates([]);
      await load();
    } catch (err) {
      setRescheduleError(err.response?.data?.error || 'Failed to reschedule. Please try again.');
    } finally {
      setRescheduleLoading(false);
    }
  };

  // ── Cancel ────────────────────────────────────────────────
  const openCancel = (delivery, kitItems) => {
    setCancelError('');
    setCancelConfirmText('');
    setCancelModal({ delivery, kitItems });
  };

  const confirmCancel = async () => {
    if (cancelConfirmText !== 'CANCEL') {
      setCancelError('Please type CANCEL to confirm.');
      return;
    }
    setCancelLoading(true);
    setCancelError('');
    try {
      await axios.patch(
        `${API_URL}/kit-orders/child/${cancelModal.delivery.id}/cancel`,
        {},
        auth()
      );
      setCancelModal(null);
      await load();
    } catch (err) {
      setCancelError(err.response?.data?.error || 'Failed to cancel. Please try again.');
    } finally {
      setCancelLoading(false);
    }
  };

  if (loading) return <p className="myk-empty">Loading your kits…</p>;
  if (error)   return <p className="myk-error">{error}</p>;
  if (parentOrders.length === 0) return (
    <p className="myk-empty">You have no kit orders yet.</p>
  );

  return (
    <div className="myk-page">
      {parentOrders.map(parent => {
        // ── Pending (Payment Requested) card ────────────────────────────
        if (parent.pending) {
          return (
            <div key={parent.parent_order_id} className="myk-parent-card myk-parent-card--pending">
              <div className="myk-pending-header">
                <div className="myk-parent-left">
                  <div>
                    <span className="myk-order-no">{parent.parent_order_no}</span>
                    <span className="myk-pending-badge">Payment Pending</span>
                  </div>
                  <span className="myk-order-date">{parent.kits.map(k => k.kit_name).join(', ')}</span>
                  {parent.parent_delivery_date && (
                    <span className="myk-order-date">
                      First delivery: {fmtDate(parent.parent_delivery_date)}
                      {parent.parent_delivery_window && ` · ${parent.parent_delivery_window}`}
                    </span>
                  )}
                  <span className="myk-order-date">
                    {parent.kits.map(k => {
                      const dp = (k.delivery_days || []).map(d => DOW[d]).join(', ');
                      return `${k.item_days}-day kit · ${k.delivery_count} deliveries${dp ? ` · ${dp}` : ''}`;
                    }).join(' | ')}
                  </span>
                </div>
                <div className="myk-pending-right">
                  <span className="myk-order-amount">₹{parseFloat(parent.parent_order_amount || 0).toFixed(0)}</span>
                  <button
                    className="myk-btn myk-btn--pay-now"
                    onClick={() => setPayingOrder(parent)}
                  >
                    Pay Now
                  </button>
                </div>
              </div>
            </div>
          );
        }

        // ── Expanded (paid) card ─────────────────────────────────────────
        const isOpen = expanded.has(parent.parent_order_id);
        return (
          <div key={parent.parent_order_id} className="myk-parent-card">
            <div className="myk-parent-header" onClick={() => toggleParent(parent.parent_order_id)}>
              <div className="myk-parent-left">
                <span className="myk-toggle">{isOpen ? '▼' : '▶'}</span>
                <div>
                  <span className="myk-order-no">{parent.parent_order_no}</span>
                  <span className="myk-order-date">{parent.kits.map(k => k.kit_name).join(', ')}</span>
                  <span className="myk-order-date">
                    First delivery: {fmtDate(
                      parent.kits.flatMap(k => k.deliveries.map(d => d.delivery_date))
                        .filter(Boolean).sort()[0]
                    )}
                  </span>
                </div>
              </div>
              <span className="myk-order-amount">₹{parseFloat(parent.parent_order_amount || 0).toFixed(0)} paid</span>
            </div>

            {isOpen && parent.kits.map(kit => {
              const delivered   = kit.deliveries.filter(d => d.order_state === 'Delivered').length;
              const cancelled   = kit.deliveries.filter(d => d.order_state === 'Cancelled').length;
              const upcoming    = kit.deliveries.filter(d => !['Delivered','Dispatched','Cancelled'].includes(d.order_state)).length;
              const delivDayNames   = (kit.delivery_days    || []).map(d => DOW[d]).join(', ');
              const consumpDayNames = (kit.consumption_days || []).map(d => DOW[d]).join(', ');
              const multiplierMap   = itemsPerDelivery(kit.delivery_days, kit.consumption_days);
              const variesQty       = Object.values(multiplierMap).some(v => v !== Object.values(multiplierMap)[0]);

              return (
                <div key={kit.kit_product_id} className="myk-kit-block">
                  <div className="myk-kit-name">{kit.kit_name}</div>
                  <div className="myk-kit-meta">
                    <span>{kit.item_days}-day kit · {kit.delivery_count} deliveries</span>
                    {consumpDayNames && <span>· consumed {consumpDayNames}</span>}
                    {delivDayNames   && <span>· delivered {delivDayNames}</span>}
                    <span>· ✅ {delivered} done</span>
                    {cancelled > 0 && <span>· ❌ {cancelled} cancelled</span>}
                    <span>· 📦 {upcoming} upcoming</span>
                  </div>

                  <div className="myk-kit-items-summary">
                    {variesQty ? 'Per serving:' : 'Each delivery:'}{' '}
                    {kit.kit_items.map((item, i) => (
                      <span key={i} className="myk-item-chip">
                        {item.product_name} ×{item.quantity}
                      </span>
                    ))}
                    {variesQty && (
                      <span style={{ color: '#888', fontSize: '0.76rem' }}>
                        (qty varies by delivery day)
                      </span>
                    )}
                  </div>

                  <table className="myk-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Date</th>
                        <th>Contents</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {kit.deliveries.map(d => {
                        const mult = d.delivery_date ? (multiplierMap[dowOf(d.delivery_date)] ?? 1) : 1;
                        return (
                        <tr key={d.id} className={d.order_state === 'Cancelled' ? 'myk-row--cancelled' : ''}>
                          <td className="myk-seq">{d.sequence_no}</td>
                          <td>
                            <span className="myk-date">{fmtDate(d.delivery_date)}</span>
                            {d.original_delivery_date && d.original_delivery_date !== d.delivery_date && (
                              <span className="myk-original-date">
                                orig: {fmtDate(d.original_delivery_date)}
                              </span>
                            )}
                          </td>
                          <td className="myk-contents">
                            {kit.kit_items.map((item, i) => (
                              <span key={i} className="myk-item-chip myk-item-chip--sm">
                                {item.product_name} ×{item.quantity * mult}
                              </span>
                            ))}
                          </td>
                          <td>
                            <span className={`myk-state myk-state--${d.order_state?.toLowerCase()}`}>
                              {STATE_ICON[d.order_state] || ''} {d.order_state}
                            </span>
                          </td>
                          <td>
                            {isActionable(d) && (
                              <div className="myk-actions">
                                <button
                                  className="myk-btn myk-btn--reschedule"
                                  onClick={() => openReschedule(d, kit.delivery_days)}
                                >
                                  Reschedule
                                </button>
                                <button
                                  className="myk-btn myk-btn--cancel"
                                  onClick={() => openCancel(d, kit.kit_items)}
                                >
                                  Cancel
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>
        );
      })}

      {/* ── Reschedule Modal ── */}
      {rescheduleModal && (
        <div className="myk-modal-overlay" onClick={() => setRescheduleModal(null)}>
          <div className="myk-modal" onClick={e => e.stopPropagation()}>
            <div className="myk-modal-header">
              <h3>Reschedule Delivery #{rescheduleModal.delivery.sequence_no}</h3>
              <button className="myk-modal-close" onClick={() => setRescheduleModal(null)}>×</button>
            </div>
            <div className="myk-modal-body">
              <p className="myk-modal-current">
                Current date: <strong>{fmtDate(rescheduleModal.delivery.delivery_date)}</strong>
              </p>
              <p className="myk-modal-sub">
                Available dates ({(rescheduleModal.scheduleDays || []).map(d => DOW[d]).join(', ') || 'any delivery day'}, within 60 days):
              </p>
              {rescheduleError && <p className="myk-error">{rescheduleError}</p>}
              {validDates.length === 0 && !rescheduleError && (
                <p className="myk-empty">No available dates found.</p>
              )}
              <div className="myk-date-grid">
                {validDates.map(date => (
                  <button
                    key={date}
                    className="myk-date-pill"
                    disabled={rescheduleLoading}
                    onClick={() => confirmReschedule(date)}
                  >
                    {fmtDate(date)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Cancel Modal ── */}
      {cancelModal && (
        <div className="myk-modal-overlay" onClick={() => setCancelModal(null)}>
          <div className="myk-modal" onClick={e => e.stopPropagation()}>
            <div className="myk-modal-header">
              <h3>Cancel Delivery #{cancelModal.delivery.sequence_no}?</h3>
              <button className="myk-modal-close" onClick={() => setCancelModal(null)}>×</button>
            </div>
            <div className="myk-modal-body">
              <p>You are cancelling the delivery of:</p>
              <ul className="myk-cancel-items">
                {cancelModal.kitItems.map((item, i) => (
                  <li key={i}>{item.product_name} ×{item.quantity}</li>
                ))}
              </ul>
              <p>scheduled for <strong>{fmtDate(cancelModal.delivery.delivery_date)}</strong>.</p>

              <div className="myk-cancel-warning">
                <p>⚠ WARNING: This delivery CANNOT be refunded or replaced.</p>
                <p>You will permanently lose this delivery from your kit. This cannot be undone.</p>
              </div>

              <label className="myk-cancel-label">
                Type <strong>CANCEL</strong> to confirm:
              </label>
              <input
                type="text"
                className="myk-cancel-input"
                value={cancelConfirmText}
                onChange={e => setCancelConfirmText(e.target.value)}
                placeholder="Type CANCEL here"
                autoComplete="off"
              />

              {cancelError && <p className="myk-error">{cancelError}</p>}

              <div className="myk-modal-footer">
                <button className="myk-btn myk-btn--back" onClick={() => setCancelModal(null)}>
                  Go Back
                </button>
                <button
                  className="myk-btn myk-btn--confirm-cancel"
                  onClick={confirmCancel}
                  disabled={cancelLoading || cancelConfirmText !== 'CANCEL'}
                >
                  {cancelLoading ? 'Cancelling…' : 'Permanently Cancel Delivery'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Kit Payment Modal ── */}
      {payingOrder && (
        <KitPaymentModal
          order={payingOrder}
          user={user}
          onClose={() => setPayingOrder(null)}
          onPaid={() => { setPayingOrder(null); load(); }}
        />
      )}
    </div>
  );
}
